import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ClientPortalAddEditClientModal } from '@/components/modals/ClientPortalAddEditClientModal';
import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
  KstDataGridHeaderButton,
} from '@/components/KstDataGrid';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';
import { hasStringValue } from '@/utils/legacy-utils';
// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const isArrayEmpty = _wg('isArrayEmpty');
const isoDatetimeToFormattedDatetime = _wg('isoDatetimeToFormattedDatetime');
const deepCopyProperties = _wg('deepCopyProperties');
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');
const removeObjectFromArrayByProperty = _wg('removeObjectFromArrayByProperty');
const generalLogEntryTemplate = _wg('generalLogEntryTemplate');
const refreshPage = _wg('refreshPage');

// ── Types ────────────────────────────────────────────────────────────

interface ClientPortalUser {
  id: number;
  username: string;
  nickname: string;
  npsdClientRowList: any[];
  isRegistered: boolean;
  lastRegistrationEmailDate: string;
  isChecked: boolean;
}

interface ClientEntry {
  id: number;
  name: string;
  hasActivityReportAccess: boolean;
  hasCaseReportAccess: boolean;
  hasCallCardAccess: boolean;
  hasAnalyticsReportAccess: boolean;
  isNpsdClientEnabled: boolean;
  isChecked: boolean;
}

interface LogEntry {
  actionedOnNickname: string;
  dateCreated: string;
  entry: string;
  action: string;
  creatorId: number;
  isChecked: boolean;
}

// ── Component ────────────────────────────────────────────────────────

export function ClientPortalAdminScreen() {
  const { theme } = useTheme();

  // ── State ──────────────────────────────────────────────────────

  // Page / Tab state
  const [pageShowing, setPageShowing] = useState('showMain');
  const [activeTab, setActiveTab] = useState('showUsersTab');

  // User grid
  const [userRows, setUserRows] = useState<KstDataGridRows>({ entries: null });
  const [userPager] = useState<KstDataGridPager>({ curPage: null as any, numPages: null as any, records: null as any, isVisible: false });

  // Client grid (within user add/edit page)
  const [clientRows, setClientRows] = useState<KstDataGridRows>({ entries: [] });
  const [clientPager] = useState<KstDataGridPager>({ curPage: null as any, numPages: null as any, records: null as any, isVisible: false });

  // Log grid
  const [logRows, setLogRows] = useState<KstDataGridRows>({ entries: [] });
  const [logPager, setLogPager] = useState<KstDataGridPager>({ curPage: null as any, numPages: null as any, records: null as any, isVisible: true });

  // User add/edit page
  const [userPageType, setUserPageType] = useState<'add' | 'edit'>('add');
  const [userPageUsername, setUserPageUsername] = useState('');
  const [userPageNickname, setUserPageNickname] = useState('');
  const [userPageUserId, setUserPageUserId] = useState<number | null>(null);

  // Add/Edit Client Modal
  const [addEditClientModal, setAddEditClientModal] = useState({
    open: false,
    mode: 'add' as 'add' | 'edit',
    clientInfo: {} as any,
  });

  // Delete users confirm modal
  const [deleteUsersConfirm, setDeleteUsersConfirm] = useState({
    open: false,
    message: '',
    usersToDelete: [] as any[],
  });
  const [isDeletingUsers, setIsDeletingUsers] = useState<boolean>(false);

  // Settings
  const [isCompanyLogoOnNav, setIsCompanyLogoOnNav] = useState<boolean | null>(null);
  const [isCompanyNameOnNav, setIsCompanyNameOnNav] = useState<boolean | null>(null);
  const [isRemovingNpsdClientWhenDisabled, setIsRemovingNpsdClientWhenDisabled] = useState<boolean | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Loading states
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isLogsLoading, setIsLogsLoading] = useState(false);

  // General
  const [clientList, setClientList] = useState<any[]>([]);

  // Mobile tabs
  const mobileTabs = useMemo(() => [
    { id: 'showUsersTab', label: 'Users' },
    { id: 'showSettingsTab', label: 'Settings' },
    { id: 'showLogTab', label: 'Log' },
  ], []);

  // ── Computed ───────────────────────────────────────────────────

  const selectedUserClientIds = useMemo(() => {
    return (clientRows.entries || []).map((entry: any) => entry.id);
  }, [clientRows.entries]);

  // ── Tooltip helper ─────────────────────────────────────────────

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const setupClientTooltips = useCallback(() => {
    if (!tooltipRef.current) {
      const el = document.createElement('div');
      el.className = 'kst-hover-tooltip';
      document.body.appendChild(el);
      tooltipRef.current = el;
    }

    const clientNames = document.querySelectorAll('.kst-client-name[data-tooltip]');
    clientNames.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      htmlEl.onmouseenter = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const tooltipText = target.getAttribute('data-tooltip');
        if (!tooltipText || !tooltipRef.current) return;
        tooltipRef.current.textContent = tooltipText;
        const rect = target.getBoundingClientRect();
        let top = rect.bottom + 8;
        let left = rect.left;
        if (left + 200 > window.innerWidth) left = window.innerWidth - 210;
        if (top + 100 > window.innerHeight) top = rect.top - 100;
        tooltipRef.current.style.top = top + 'px';
        tooltipRef.current.style.left = left + 'px';
        tooltipRef.current.classList.add('kst-hover-tooltip--visible');
      };
      htmlEl.onmouseleave = () => {
        if (tooltipRef.current) {
          tooltipRef.current.classList.remove('kst-hover-tooltip--visible');
        }
      };
    });
  }, []);

  // Cleanup tooltip on unmount
  useEffect(() => {
    return () => {
      if (tooltipRef.current && tooltipRef.current.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      }
    };
  }, []);

  // Re-attach tooltips after user rows render
  useEffect(() => {
    if (userRows.entries && userRows.entries.length > 0) {
      setupClientTooltips();
    }
  }, [userRows, setupClientTooltips]);

  // ── Column Definitions ─────────────────────────────────────────

  const userColumns: KstDataGridColumn[] = useMemo(() => [
    { field: 'username', name: 'Email', size: 'col-xs-3' },
    { field: 'nickname', name: 'Nickname', size: 'col-xs-2' },
    {
      field: 'npsdClientRowList', name: 'Clients', size: 'col-xs-2',
      template: (e: any) => {
        const clientSpans = e.npsdClientRowList.map((client: any) => {
          const accessInfo = `Activity Reports: ${client.hasActivityReportAccess ? 'Yes' : 'No'}\nCase Reports: ${client.hasCaseReportAccess ? 'Yes' : 'No'}\nCalls For Service: ${client.hasCallCardAccess ? 'Yes' : 'No'}\nAnalytics Reports: ${client.hasAnalyticsReportAccess ? 'Yes' : 'No'}`;
          const displayName = client.isNpsdClientEnabled ? client.name : (client.name + ' (disabled)');
          const disabledClass = client.isNpsdClientEnabled ? '' : 'kst-client-disabled';
          return `<span class="kst-client-name ${disabledClass}" data-tooltip="${accessInfo}">${displayName}</span>`;
        });
        return clientSpans.join(', ');
      },
    },
    {
      field: 'isRegistered', name: 'Registration', size: 'col-xs-3',
      template: (e: any) => {
        if (e.isRegistered) {
          return '<span class="kst-status-registered"><svg class="kst-icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg> Registered</span>';
        }
        const formattedDate = isoDatetimeToFormattedDatetime(e.lastRegistrationEmailDate, (window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT);
        return `<a class="kst-resend-link" data-resend-id="${e.id}">Resend Registration Email</a> <br /> Last sent: ${formattedDate}.`;
      },
    },
    { field: 'id', name: 'ID', size: 'hidden' },
  ], []);

  const userMobileColumns: KstDataGridColumn[] = useMemo(() => [
    {
      field: 'npsdClientRowList', name: 'Clients', size: 'col-xs-6',
      template: (e: any) => {
        const names = e.npsdClientRowList.map((client: any) =>
          client.isNpsdClientEnabled ? client.name : (client.name + ' (disabled)')
        );
        return names.join(', ');
      },
    },
    {
      field: 'isRegistered', name: 'Status', size: 'col-xs-4',
      template: (e: any) => {
        if (e.isRegistered) return '<span class="kst-status-registered">Registered</span>';
        return '<span class="kst-status-pending">Pending</span>';
      },
    },
  ], []);

  const userHeaderButtons: KstDataGridHeaderButton[] = useMemo(() => [
    { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
    { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
    { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
  ], []);

  const clientColumns: KstDataGridColumn[] = useMemo(() => [
    {
      field: 'name', name: 'Name', size: 'col-xs-3',
      template: (e: any) => e.isNpsdClientEnabled ? e.name : e.name + ' (disabled)',
    },
    {
      field: 'hasActivityReportAccess', name: 'Activity Reports', size: 'col-xs-2',
      template: (e: any) => e.hasActivityReportAccess ? '<span class="kst-access-yes">Yes</span>' : '',
    },
    {
      field: 'hasCaseReportAccess', name: 'Case Reports', size: 'col-xs-2',
      template: (e: any) => e.hasCaseReportAccess ? '<span class="kst-access-yes">Yes</span>' : '',
    },
    {
      field: 'hasCallCardAccess', name: 'Calls For Service', size: 'col-xs-2',
      template: (e: any) => e.hasCallCardAccess ? '<span class="kst-access-yes">Yes</span>' : '',
    },
    {
      field: 'hasAnalyticsReportAccess', name: 'Analytics Reports', size: 'col-xs-2',
      template: (e: any) => e.hasAnalyticsReportAccess ? '<span class="kst-access-yes">Yes</span>' : '',
    },
    { field: 'id', name: 'ID', size: 'hidden' },
  ], []);

  const clientHeaderButtons: KstDataGridHeaderButton[] = useMemo(() => [
    { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
    { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
    { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
  ], []);

  const logColumns: KstDataGridColumn[] = useMemo(() => [
    { field: 'actionedOnNickname', name: 'Nickname', size: 'col-xs-2' },
    {
      field: 'dateCreated', name: 'Date/Time', size: 'col-xs-2',
      template: (e: any) => moment(e.dateCreated).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT).toString(),
    },
    {
      field: 'entry', name: 'Entry', size: 'col-xs-4',
      template: (e: any) => generalLogEntryTemplate(e.entry),
    },
    { field: 'action', name: 'Action', size: 'col-xs-4' },
    { field: 'creatorId', name: 'ID', size: 'hidden' },
  ], []);

  // ── Data Loading ───────────────────────────────────────────────

  const refreshUserGrid = useCallback(() => {
    setIsUsersLoading(true);
    api.getClientPortalUserList((result: any) => {
      const entries = result.map((item: any) => ({
        id: item.id,
        username: item.username,
        nickname: item.nickname,
        npsdClientRowList: item.npsdClientRowList,
        isRegistered: item.isRegistered,
        lastRegistrationEmailDate: item.lastRegistrationEmailDate,
        isChecked: false,
      }));
      setUserRows({ entries });
      setIsUsersLoading(false);
    }, () => {
      setIsUsersLoading(false);
    });
  }, []);

  const getLogs = useCallback((pg: number) => {
    setIsLogsLoading(true);
    api.getClientPortalLogEntries(
      `{"data":{"take":30,"skip":0,"page":${pg},"pageSize":30}}`,
      false,
      (result: any) => {
        const entries = result.logs.map((entry: any) => ({
          ...entry,
          isChecked: false,
        }));
        setLogRows({ entries });
        setLogPager({
          curPage: result.curPage,
          numPages: result.numPages,
          records: result.records,
          isVisible: true,
        });
        setIsLogsLoading(false);
      },
      () => {
        App.showPopupNotification('Error getting logs.', 'error');
        setIsLogsLoading(false);
      }
    );
  }, []);

  // Initial load
  useEffect(() => {
    refreshUserGrid();
  }, [refreshUserGrid]);

  // Handle resend registration email clicks via event delegation
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const resendId = target.getAttribute('data-resend-id');
      if (resendId) {
        e.preventDefault();
        api.resendClientPortalRegistration(parseInt(resendId), () => {
          App.showPopupNotification('Email Sent.', 'success');
          refreshUserGrid();
        }, () => {
          App.showPopupNotification('Error re-sending email.', 'error');
        });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [refreshUserGrid]);

  // ── Tab Navigation ─────────────────────────────────────────────

  const showUsersPage = useCallback(() => {
    setPageShowing('showMain');
    setActiveTab('showUsersTab');
  }, []);

  const showSettingsPage = useCallback(() => {
    setSettingsLoaded(false);
    setIsCompanyLogoOnNav(null);
    setIsCompanyNameOnNav(null);
    setIsRemovingNpsdClientWhenDisabled(null);
    setPageShowing('showSettings');
    setActiveTab('showSettingsTab');

    api.getClientPortalSettings(
      (successResult: any) => {
        setIsCompanyLogoOnNav(successResult.isCompanyLogoOnNav);
        setIsCompanyNameOnNav(successResult.isCompanyNameOnNav);
        setIsRemovingNpsdClientWhenDisabled(successResult.isRemovingNpsdClientWhenDisabled);
        setSettingsLoaded(true);
      },
      () => {
        App.showPopupNotification('Error getting settings', 'error');
      }
    );
  }, []);

  const showLogPage = useCallback(() => {
    setPageShowing('showLog');
    setActiveTab('showLogTab');
    getLogs(1);
  }, [getLogs]);

  const onMobileTabChange = useCallback((tabId: string) => {
    const tabMethods: Record<string, () => void> = {
      showUsersTab: showUsersPage,
      showSettingsTab: showSettingsPage,
      showLogTab: showLogPage,
    };
    tabMethods[tabId]?.();
  }, [showUsersPage, showSettingsPage, showLogPage]);

  // ── Settings Methods ───────────────────────────────────────────

  const updateClientPortalSettings = useCallback(() => {
    const data = {
      isCompanyLogoOnNav,
      isCompanyNameOnNav,
      isRemovingNpsdClientWhenDisabled,
    };
    api.updateClientPortalSettings(
      data,
      () => App.showPopupNotification('Settings saved.', 'success'),
      () => App.showPopupNotification('Error updating settings. Please try again.', 'error')
    );
  }, [isCompanyLogoOnNav, isCompanyNameOnNav, isRemovingNpsdClientWhenDisabled]);

  // ── User Page Methods ──────────────────────────────────────────

  const setUserPageClients = useCallback((clientInfoList: any[]) => {
    const entries = clientInfoList.map((ci) => {
      const row = deepCopyProperties(ci);
      row.isChecked = false;
      return row;
    });
    setClientRows({ entries });
  }, []);

  const ensureClientListLoaded = useCallback((callback: () => void) => {
    if (isArrayEmpty(clientList)) {
      api.listClientGroup((result: any) => {
        setClientList(result.npsdClientList);
        callback();
      }, () => {
        App.showPopupNotification('Error getting client list.', 'error');
      }, false);
    } else {
      callback();
    }
  }, [clientList]);

  const addUserDialog = useCallback(() => {
    ensureClientListLoaded(() => {
      setUserPageType('add');
      setUserPageUserId(null);
      setUserPageUsername('');
      setUserPageNickname('');
      setUserPageClients([]);
      setPageShowing('showAddEdit');
    });
  }, [ensureClientListLoaded, setUserPageClients]);

  const editUserDialog = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select the user to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one user may be edited at a time.', 'warning');
      return;
    }

    ensureClientListLoaded(() => {
      const item = checkedItems[0];
      setUserPageType('edit');
      setUserPageUserId(item.id);
      setUserPageUsername(item.username);
      setUserPageNickname(item.nickname);

      const selectedClientInfoList = item.npsdClientRowList.map((e: any) => ({
        id: e.npsdClientId,
        name: e.name,
        hasActivityReportAccess: e.hasActivityReportAccess,
        hasCaseReportAccess: e.hasCaseReportAccess,
        hasCallCardAccess: e.hasCallCardAccess,
        hasAnalyticsReportAccess: e.hasAnalyticsReportAccess,
        isNpsdClientEnabled: e.isNpsdClientEnabled,
      }));
      setUserPageClients(selectedClientInfoList);
      setPageShowing('showAddEdit');
    });
  }, [ensureClientListLoaded, setUserPageClients]);

  const deleteUserDialog = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select the user to delete by clicking the checkbox.', 'warning');
      return;
    }
    const userNames = checkedItems.map((u) => u.nickname).join(', ');
    setDeleteUsersConfirm({
      open: true,
      message: `Are you sure you want to delete the following users: ${userNames}?`,
      usersToDelete: checkedItems,
    });
  }, []);

  const confirmDeleteUsers = useCallback(() => {
    const users = deleteUsersConfirm.usersToDelete;
    const len = users.length;
    let count = 0;
    setIsDeletingUsers(true);
    for (let i = 0; i < len; i++) {
      api.deleteClientPortalUser(users[i].id, () => {
        count++;
        if (count === len) {
          setIsDeletingUsers(false);
          setDeleteUsersConfirm({ open: false, message: '', usersToDelete: [] });
          refreshUserGrid();
        }
      }, () => {
        setIsDeletingUsers(false);
        App.showPopupNotification('Error deleting user.', 'error');
        refreshPage();
      });
    }
  }, [deleteUsersConfirm.usersToDelete, refreshUserGrid]);

  const cancelDeleteUsers = useCallback(() => {
    setDeleteUsersConfirm({ open: false, message: '', usersToDelete: [] });
  }, []);

  const saveOrUpdateUserPage = useCallback(() => {
    if (!hasStringValue(userPageNickname)) {
      App.showPopupNotification('Nickname required.', 'error');
      return;
    }

    const npsdClientInfoList = clientRows.entries.map((entry: any) => ({
      npsdClientId: entry.id,
      hasActivityReportAccess: entry.hasActivityReportAccess,
      hasCaseReportAccess: entry.hasCaseReportAccess,
      hasCallCardAccess: entry.hasCallCardAccess,
      hasAnalyticsReportAccess: entry.hasAnalyticsReportAccess,
    }));

    if (userPageType === 'add') {
      if (!hasStringValue(userPageUsername)) {
        App.showPopupNotification('Email required.', 'error');
        return;
      }
      const data = { username: userPageUsername, nickname: userPageNickname, npsdClientInfoList };
      api.addClientPortalUser(data, () => {
        setPageShowing('showMain');
        refreshUserGrid();
      }, () => {
        App.showPopupNotification('Error adding user.', 'error');
      });
    } else if (userPageType === 'edit') {
      const data = { id: userPageUserId, nickname: userPageNickname, npsdClientInfoList };
      api.editClientPortalUser(data, () => {
        setPageShowing('showMain');
        refreshUserGrid();
      }, () => {
        App.showPopupNotification('Error editing user.', 'error');
      });
    }
  }, [userPageType, userPageUsername, userPageNickname, userPageUserId, clientRows.entries, refreshUserGrid]);

  const cancelUserPage = useCallback(() => {
    setPageShowing('showMain');
  }, []);

  // ── Client Dialog Methods (within user edit page) ──────────────

  const addClientDialog = useCallback(() => {
    setAddEditClientModal({ open: true, mode: 'add', clientInfo: {} });
  }, []);

  const editClientDialog = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select the client to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one client may be edited at a time.', 'warning');
      return;
    }
    setAddEditClientModal({
      open: true,
      mode: 'edit',
      clientInfo: {
        id: checkedItems[0].id,
        name: checkedItems[0].name,
        hasActivityReportAccess: checkedItems[0].hasActivityReportAccess,
        hasCaseReportAccess: checkedItems[0].hasCaseReportAccess,
        hasCallCardAccess: checkedItems[0].hasCallCardAccess,
        hasAnalyticsReportAccess: checkedItems[0].hasAnalyticsReportAccess,
      },
    });
  }, []);

  const handleAddEditClientSave = useCallback((data: any) => {
    if (addEditClientModal.mode === 'add') {
      setClientRows((prev) => ({
        entries: [
          ...prev.entries,
          {
            id: data.id,
            name: data.name,
            hasActivityReportAccess: data.hasActivityReportAccess,
            hasCaseReportAccess: data.hasCaseReportAccess,
            hasCallCardAccess: data.hasCallCardAccess,
            hasAnalyticsReportAccess: data.hasAnalyticsReportAccess,
            isNpsdClientEnabled: true,
            isChecked: false,
          },
        ],
      }));
    } else {
      const originalId = data.originalId;
      setClientRows((prev) => ({
        entries: prev.entries.map((row: any) => {
          if (row.id === originalId) {
            return {
              ...row,
              id: data.id,
              name: data.name,
              hasActivityReportAccess: data.hasActivityReportAccess,
              hasCaseReportAccess: data.hasCaseReportAccess,
              hasCallCardAccess: data.hasCallCardAccess,
              hasAnalyticsReportAccess: data.hasAnalyticsReportAccess,
              isNpsdClientEnabled: originalId !== data.id ? true : row.isNpsdClientEnabled,
            };
          }
          return row;
        }),
      }));
    }
    setAddEditClientModal((prev) => ({ ...prev, open: false }));
  }, [addEditClientModal.mode]);

  const handleAddEditClientCancel = useCallback(() => {
    setAddEditClientModal((prev) => ({ ...prev, open: false }));
  }, []);

  const deleteClientFromUser = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select the client to delete by clicking the checkbox.', 'warning');
      return;
    }
    const idsToRemove = checkedItems.map((ci) => ci.id);
    setClientRows((prev) => ({
      entries: prev.entries.filter((entry: any) => !idsToRemove.includes(entry.id)),
    }));
  }, []);

  // ── User Grid Button Command ───────────────────────────────────

  const handleUserGridCommand = useCallback((functionName: string, checkedItems: any[]) => {
    switch (functionName) {
      case 'add': addUserDialog(); break;
      case 'edit': editUserDialog(checkedItems); break;
      case 'delete': deleteUserDialog(checkedItems); break;
    }
  }, [addUserDialog, editUserDialog, deleteUserDialog]);

  // ── Client Grid Button Command ─────────────────────────────────

  const handleClientGridCommand = useCallback((functionName: string, checkedItems: any[]) => {
    switch (functionName) {
      case 'add': addClientDialog(); break;
      case 'edit': editClientDialog(checkedItems); break;
      case 'delete': deleteClientFromUser(checkedItems); break;
    }
  }, [addClientDialog, editClientDialog, deleteClientFromUser]);

  // ── Log Pager ──────────────────────────────────────────────────

  const handleLogGoToPage = useCallback((pg: number) => {
    getLogs(pg);
  }, [getLogs]);

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className={theme}>
      {/* Modals */}
      <ClientPortalAddEditClientModal
        open={addEditClientModal.open}
        mode={addEditClientModal.mode}
        clientInfo={addEditClientModal.clientInfo}
        clientList={clientList}
        selectedClientIds={selectedUserClientIds}
        theme={theme}
        onSave={handleAddEditClientSave}
        onCancel={handleAddEditClientCancel}
      />

      <ConfirmModal
        open={deleteUsersConfirm.open}
        title="Delete Users"
        message={deleteUsersConfirm.message}
        confirmText="Delete"
        confirmDisabled={isDeletingUsers}
        busy={isDeletingUsers}
        theme={theme}
        onConfirm={confirmDeleteUsers}
        onCancel={cancelDeleteUsers}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={mobileTabs} activeTab={activeTab} onTabChange={onMobileTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showUsersTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={showUsersPage}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Users</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showSettingsTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={showSettingsPage}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Settings</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showLogTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={showLogPage}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">Log</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Add/Edit User Page */}
            {pageShowing === 'showAddEdit' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">
                    {userPageType === 'add' ? 'Add Client Portal User' : 'Edit Client Portal User'}
                  </h3>
                </div>

                <div className="kst-form-section">
                  <div className="kstc-form-group">
                    <label className="kstc-label kstc-label--required" htmlFor="cpUserEmail">Email</label>
                    <input
                      id="cpUserEmail"
                      type="text"
                      className="kstc-input"
                      maxLength={255}
                      placeholder="Email (e.g. john.smith@my-client.com)"
                      value={userPageUsername}
                      disabled={userPageType === 'edit'}
                      onChange={(e) => setUserPageUsername(e.target.value)}
                    />
                  </div>

                  <div className="kstc-form-group">
                    <label className="kstc-label kstc-label--required" htmlFor="cpUserNickname">Nickname</label>
                    <input
                      id="cpUserNickname"
                      type="text"
                      className="kstc-input"
                      maxLength={255}
                      placeholder="Nickname (e.g. John Smith, Riverdale Apartments, etc.)"
                      value={userPageNickname}
                      onChange={(e) => setUserPageNickname(e.target.value)}
                    />
                  </div>

                  <div className="kstc-form-group">
                    <label className="kstc-label kstc-label--required">Clients</label>
                    <KstDataGrid
                      columns={clientColumns}
                      rows={clientRows}
                      headerbuttons={clientHeaderButtons}
                      pager={clientPager}
                      showcheckbox={true}
                      mobiletitlefield="name"
                      onButtonCommand={handleClientGridCommand}
                    />
                  </div>

                  <div className="kst-form-actions">
                    <button type="button" className="kstc-btn kstc-btn--primary" onClick={saveOrUpdateUserPage}>
                      Save
                    </button>
                    <button type="button" className="kstc-btn kstc-btn--secondary" onClick={cancelUserPage}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Users List Page */}
            {pageShowing === 'showMain' && activeTab === 'showUsersTab' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Client Portal Management</h3>
                  <p className="kst-page-description">
                    Add or update users for access to the Client Portal. Client Portal users log in with their email address, cannot access Orna, and do not add to your bill.
                  </p>
                </div>

                <KstDataGrid
                  columns={userColumns}
                  rows={userRows}
                  headerbuttons={userHeaderButtons}
                  pager={userPager}
                  showcheckbox={true}
                  mobiletitlefield="nickname"
                  mobilecolumns={userMobileColumns}
                  loading={isUsersLoading}
                  onButtonCommand={handleUserGridCommand}
                />
              </div>
            )}

            {/* Log Page */}
            {pageShowing === 'showLog' && activeTab === 'showLogTab' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Client Portal Log</h3>
                </div>

                <KstDataGrid
                  columns={logColumns}
                  rows={logRows}
                  pager={logPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="actionedOnNickname"
                  loading={isLogsLoading}
                  onGoToPage={handleLogGoToPage}
                />
              </div>
            )}

            {/* Settings Page */}
            {pageShowing === 'showSettings' && activeTab === 'showSettingsTab' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Client Portal Settings</h3>
                  <p className="kst-page-description">
                    Clients will see updated settings on their next login. The company logo can be changed from &ldquo;Company Profile&rdquo;.
                  </p>
                </div>

                {settingsLoaded ? (
                  <div className="kst-settings-form">
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkClientPortalShowLogoNav"
                        className="kstc-checkbox"
                        checked={isCompanyLogoOnNav || false}
                        onChange={(e) => setIsCompanyLogoOnNav(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkClientPortalShowLogoNav">
                        Show your company logo on navbar
                      </label>
                    </div>
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkClientPortalShowNameNav"
                        className="kstc-checkbox"
                        checked={isCompanyNameOnNav || false}
                        onChange={(e) => setIsCompanyNameOnNav(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkClientPortalShowNameNav">
                        Show your company name on navbar
                      </label>
                    </div>
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkClientPortalRemoveNpsdClientOnDisableNav"
                        className="kstc-checkbox"
                        checked={isRemovingNpsdClientWhenDisabled || false}
                        onChange={(e) => setIsRemovingNpsdClientWhenDisabled(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkClientPortalRemoveNpsdClientOnDisableNav">
                        Remove client from client portal users when client is disabled
                      </label>
                    </div>
                    <div className="kst-settings-actions">
                      <button type="button" className="kstc-btn kstc-btn--primary" onClick={updateClientPortalSettings}>
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="kst-loading"></div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

